/**
 * ActionCard — one of the three big "what would you like to do?" tiles
 * on the home dashboard.
 *
 * Designed as a full-bleed clickable card: the entire surface is the
 * link target, with a generous touch area for mobile, soft shadow on
 * hover, and a subtle accent border on focus.
 */

import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { cn } from '@/utils/cn';

export interface ActionCardProps {
  to: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  cta: string;
  /** Optional accent color class for the icon plate. */
  accentClass?: string;
}

export function ActionCard({ to, icon, title, description, cta, accentClass }: ActionCardProps) {
  return (
    <Link
      to={to}
      className={cn(
        'group flex h-full flex-col rounded-xl border border-border bg-surface p-6 shadow-subtle',
        'transition-all duration-150 ease-emphasized',
        'hover:-translate-y-0.5 hover:border-border-strong hover:shadow-raised',
        'focus-visible:-translate-y-0.5 focus-visible:border-spark focus-visible:shadow-raised',
      )}
    >
      <div
        className={cn(
          'mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg',
          accentClass ?? 'bg-spark-soft text-spark',
        )}
        aria-hidden
      >
        {icon}
      </div>
      <h3 className="text-lg font-semibold tracking-tight text-ink">{title}</h3>
      <p className="mt-1 text-sm text-ink-muted">{description}</p>
      <div className="mt-6 flex items-center gap-2 text-sm font-medium text-spark">
        <span>{cta}</span>
        <ArrowRight
          className="h-4 w-4 transition-transform group-hover:translate-x-0.5 rtl:rotate-180 rtl:group-hover:-translate-x-0.5"
          aria-hidden
        />
      </div>
    </Link>
  );
}
